/* eslint-disable no-console */
// Using Web Crypto API for secure encryption/decryption
const SALT_LENGTH = 16;
const IV_LENGTH = 12;
const KEY_LENGTH = 256;
const ITERATIONS = 1000;

async function getKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const baseKey = await crypto.subtle.importKey('raw', encoder.encode(password), 'PBKDF2', false, [
    'deriveKey'
  ]);

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: ITERATIONS,
      hash: 'SHA-256'
    },
    baseKey,
    { name: 'AES-GCM', length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
}

export async function encrypt(data: any): Promise<string> {
  try {
    const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    const encoder = new TextEncoder();
    const encodedData = encoder.encode(JSON.stringify(data));

    // Use a constant secret key (in production, this should be in env vars)
    const secretKey = 'your-secret-key-2024';
    const key = await getKey(secretKey, salt);

    const encryptedContent = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv
      },
      key,
      encodedData
    );

    // Combine salt, iv, and encrypted content
    const encryptedArray = new Uint8Array(salt.length + iv.length + encryptedContent.byteLength);
    encryptedArray.set(salt, 0);
    encryptedArray.set(iv, salt.length);
    encryptedArray.set(new Uint8Array(encryptedContent), salt.length + iv.length);

    // Convert to base64 for storage
    return btoa(Array.from(encryptedArray, (byte) => String.fromCharCode(byte)).join(''));
  } catch (error) {
    console.error('Encryption error:', error);
    throw new Error('Failed to encrypt data');
  }
}

export async function decrypt(encryptedData: string): Promise<any> {
  try {
    // Convert from base64
    const encryptedArray = new Uint8Array(
      atob(encryptedData)
        .split('')
        .map((char) => char.charCodeAt(0))
    );

    // Extract salt, iv, and encrypted content
    const salt = encryptedArray.slice(0, SALT_LENGTH);
    const iv = encryptedArray.slice(SALT_LENGTH, SALT_LENGTH + IV_LENGTH);
    const content = encryptedArray.slice(SALT_LENGTH + IV_LENGTH);

    // Use the same secret key as encryption
    const secretKey = 'your-secret-key-2024';
    const key = await getKey(secretKey, salt);

    const decryptedContent = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv
      },
      key,
      content
    );

    const decoder = new TextDecoder();
    const decodedData = decoder.decode(decryptedContent);
    return JSON.parse(decodedData);
  } catch (error) {
    console.error('Decryption error:', error);
    return null;
  }
}
